import { OperatorFunction } from 'rxjs';
import { filter } from 'rxjs/operators';

import { MessageType } from './message-type.enum';

/**
 * Message thats send to or received from the child app in the iframe
 * @deprecated Please use the new iframe messages instead
 */
export interface Message {
	/** Type of the send or received message */
	type: MessageType;
	/** The message payload */
	payload?: any;
	/** optional url */
	url?: string;
}

/**
 * Used ngrx as example for the code below. https://github.com/ngrx/platform/blob/master/modules/store/src/action_creator.ts
 */

export interface IframeMessage {
	type: string;
}

// declare to make it property-renaming safe
export declare interface TypedIframeMessage<T extends string> extends IframeMessage {
	readonly type: T;
}

/**
 * A function that returns an object in the shape of the `Action` interface.  Configured using `createAction`.
 */
export type Creator<P extends any[] = any[], R extends object = object> = FunctionWithParametersType<P, R>;

/**
 * See `Creator`.
 */
export type IframeMessageCreator<T extends string = string, C extends Creator = Creator> = C & TypedIframeMessage<T>;

export interface IframeMessageCreatorProps<T> {
	_as: 'props';
	_p: T;
}

export const arraysAreNotAllowedMsg = 'action creator cannot return an array';
type ArraysAreNotAllowed = typeof arraysAreNotAllowedMsg;

export const typePropertyIsNotAllowedMsg = 'action creator cannot return an object with a property named `type`';
type TypePropertyIsNotAllowed = typeof typePropertyIsNotAllowedMsg;

export const emptyObjectsAreNotAllowedMsg = 'action creator cannot return an empty object';
type EmptyObjectsAreNotAllowed = typeof emptyObjectsAreNotAllowedMsg;

export const arraysAreNotAllowedInProps = 'action creator props cannot be an array';
type ArraysAreNotAllowedInProps = typeof arraysAreNotAllowedInProps;

export const typePropertyIsNotAllowedInProps = 'action creator props cannot have a property named `type`';
type TypePropertyIsNotAllowedInProps = typeof typePropertyIsNotAllowedInProps;

export const emptyObjectsAreNotAllowedInProps = 'action creator props cannot be an empty object';
type EmptyObjectsAreNotAllowedInProps = typeof emptyObjectsAreNotAllowedInProps;

export const primitivesAreNotAllowedInProps = 'action creator props cannot be a primitive value';
type PrimitivesAreNotAllowedInProps = typeof primitivesAreNotAllowedInProps;

export type NotAllowedCheck<T extends object> = T extends any[]
	? ArraysAreNotAllowed
	: T extends { type: any }
	? TypePropertyIsNotAllowed
	: keyof T extends never
	? EmptyObjectsAreNotAllowed
	: unknown;

export type FunctionWithParametersType<P extends unknown[], R = void> = (...args: P) => R;

export type Primitive = string | number | bigint | boolean | symbol | null | undefined;

export type NotAllowedInPropsCheck<T> = T extends object
	? T extends any[]
		? ArraysAreNotAllowedInProps
		: T extends { type: any }
		? TypePropertyIsNotAllowedInProps
		: keyof T extends never
		? EmptyObjectsAreNotAllowedInProps
		: unknown
	: T extends Primitive
	? PrimitivesAreNotAllowedInProps
	: never;

export function props<P extends SafeProps, SafeProps = NotAllowedInPropsCheck<P>>(): IframeMessageCreatorProps<P> {
	// tslint:disable-next-line:no-non-null-assertion
	return { _as: 'props', _p: undefined! };
}

function defineType<T extends string>(type: T, creator: Creator): IframeMessageCreator<T> {
	return Object.defineProperty(creator, 'type', {
		value: type,
		writable: false,
	}) as IframeMessageCreator<T>;
}

export function createIframeMessage<T extends string>(type: T): IframeMessageCreator<T, () => TypedIframeMessage<T>>;
export function createIframeMessage<T extends string, P extends object>(
	type: T,
	config: IframeMessageCreatorProps<P> & NotAllowedCheck<P>
): IframeMessageCreator<T, (props: P & NotAllowedCheck<P>) => P & TypedIframeMessage<T>>;

export function createIframeMessage<T extends string, C extends Creator>(type: T, config?: { _as: 'props' } | C): IframeMessageCreator<T> {
	if (typeof config === 'function') {
		return defineType(type, (...args: any[]) => ({
			...config(...args),
			type,
		}));
	}
	const as = config ? config._as : 'empty';
	switch (as) {
		case 'empty':
			return defineType(type, () => ({ type }));
		case 'props':
			// tslint:disable-next-line:no-shadowed-variable
			return defineType(type, (props: object) => ({
				...props,
				// TODO: Remove payload below, should be temporary for backward compatibility
				payload: {
					...props,
				},
				type,
			}));
		default:
			throw new Error('Unexpected config.');
	}
}

export function ofType<
	AC extends IframeMessageCreator<string, Creator>[],
	U extends IframeMessage = IframeMessage,
	V = ReturnType<AC[number]>
>(...allowedTypes: AC): OperatorFunction<U, V>;
export function ofType(
	...allowedTypes: Array<string | IframeMessageCreator<string, Creator>>
): OperatorFunction<IframeMessage, IframeMessage> {
	return filter((action: IframeMessage) =>
		allowedTypes.some(typeOrActionCreator => {
			if (typeof typeOrActionCreator === 'string') {
				// Comparing the string to type
				return typeOrActionCreator === action.type;
			} else {
				// We are filtering by ActionCreator
				return typeOrActionCreator.type === action.type;
			}
		})
	);
}
